import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
    ArrowLeft, Save, Eye, EyeOff, ChevronDown,
    Loader2, FileText, Mail, Type, AlignLeft,
    MessageSquare, Sparkles, Send, Bot, User,
    GripVertical,
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import RichTextEditor from '../components/RichTextEditor';

const PLACEHOLDERS = [
    { label: 'Employee First Name', value: '{employee.first_name}' },
    { label: 'Employee Full Name', value: '{employee.full_name}' },
    { label: 'Employee Email', value: '{employee.email}' },
    { label: 'Start Date', value: '{employee.start_date}' },
    { label: 'Team', value: '{employee.team}' },
    { label: 'Manager Name', value: '{employee.manager.name}' },
    { label: 'Company Name', value: '{company.name}' },
    { label: 'Company Website', value: '{company.website}' },
    { label: 'Sender Name', value: '{sender.name}' },
    { label: 'Sender Title', value: '{sender.title}' },
    { label: 'Sender Email', value: '{sender.email}' },
];

// Render text with placeholder tokens highlighted as violet chips
const renderWithPlaceholderChips = (text) => {
    const parts = text.split(/(\{[a-zA-Z0-9_.]+\})/g);
    return parts.map((part, i) =>
        /^\{[a-zA-Z0-9_.]+\}$/.test(part) ? (
            <span
                key={i}
                className="inline-block bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 rounded px-1 py-0.5 text-xs font-medium border border-violet-200 dark:border-violet-500/30 mx-0.5"
            >
                {part}
            </span>
        ) : (
            <span key={i}>{part}</span>
        )
    );
};

// Auto-suggest input: shows placeholder dropdown when user types "{"
const AutosuggestInput = ({ inputRef, value, onChange, onFocus, placeholder, className, icon: Icon }) => {
    const [suggest, setSuggest] = useState(null); // { query, cursorPos }
    const dropdownRef = useRef(null);

    const handleInputChange = (e) => {
        const val = e.target.value;
        const cursor = e.target.selectionStart || 0;
        onChange(val);

        // Find if cursor is inside an incomplete placeholder: look back for "{"
        const textBefore = val.substring(0, cursor);
        const lastOpen = textBefore.lastIndexOf('{');
        const lastClose = textBefore.lastIndexOf('}');

        if (lastOpen > lastClose) {
            const query = textBefore.substring(lastOpen + 1);
            // Only suggest if query looks like a partial placeholder (no spaces)
            if (!/\s/.test(query)) {
                setSuggest({ query, cursorPos: cursor, bracePos: lastOpen });
                return;
            }
        }
        setSuggest(null);
    };

    const handleKeyDown = (e) => {
        if (suggest && e.key === 'Escape') {
            e.stopPropagation();
            setSuggest(null);
        }
    };

    const handleSelect = (placeholder) => {
        const before = value.substring(0, suggest.bracePos);
        const after = value.substring(suggest.cursorPos);
        const newValue = before + placeholder.value + after;
        onChange(newValue);
        setSuggest(null);

        // Restore cursor after the inserted placeholder
        setTimeout(() => {
            const ref = inputRef?.current;
            if (ref) {
                const newPos = before.length + placeholder.value.length;
                ref.focus();
                ref.setSelectionRange(newPos, newPos);
            }
        }, 0);
    };

    const filtered = suggest
        ? PLACEHOLDERS.filter(p => {
            const q = suggest.query.toLowerCase();
            return p.value.toLowerCase().includes(q) || p.label.toLowerCase().includes(q);
        })
        : [];

    // Close on outside click
    useEffect(() => {
        if (!suggest) return;
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setSuggest(null);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [suggest]);

    return (
        <div className="relative">
            {Icon && <Icon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
            <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={onFocus}
                placeholder={placeholder}
                className={className}
            />
            {suggest && filtered.length > 0 && (
                <div
                    ref={dropdownRef}
                    className="absolute z-30 mt-1 left-0 right-0 bg-card border border-border rounded-xl shadow-xl py-1.5 max-h-52 overflow-y-auto"
                >
                    <div className="px-3 py-1 text-[10px] text-muted-foreground font-medium uppercase tracking-wider flex items-center gap-1.5">
                        <Type size={10} />
                        Placeholders
                    </div>
                    {filtered.map(p => (
                        <button
                            key={p.value}
                            type="button"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelect(p);
                            }}
                            className="w-full text-left px-3 py-1.5 hover:bg-violet-50 dark:hover:bg-violet-500/10 transition-colors flex items-center justify-between gap-2"
                        >
                            <span className="text-xs text-foreground">{p.label}</span>
                            <code className="text-[10px] text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-500/10 rounded px-1.5 py-0.5 font-medium">{p.value}</code>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const EmailTemplateEditor = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const isNew = id === 'new';
    const defaultType = searchParams.get('type') || 'onboarding';

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        template_type: defaultType,
        subject: '',
        greeting: '',
        body: '',
        closing: '',
        signature: '',
        is_default: false,
    });
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);

    // Preview
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Placeholder dropdown
    const [showPlaceholders, setShowPlaceholders] = useState(false);
    const [activeField, setActiveField] = useState('body');
    const fieldRefs = {
        name: useRef(null),
        subject: useRef(null),
        greeting: useRef(null),
        body: useRef(null),
        closing: useRef(null),
        signature: useRef(null),
    };

    // AI Chat
    const [showAI, setShowAI] = useState(false);
    const [aiMessages, setAiMessages] = useState([]);
    const [aiInput, setAiInput] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const aiChatEndRef = useRef(null);

    // Resizable split panel (40/60 default: 40% AI chat, 60% editor)
    const [splitRatio, setSplitRatio] = useState(() => {
        const saved = localStorage.getItem('email_template_split_ratio');
        return saved ? parseFloat(saved) : 40;
    });
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef(null);
    const MIN_CHAT_WIDTH = 280;
    const MIN_EDITOR_WIDTH = 380;

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || !containerRef.current) return;
        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const mouseX = e.clientX - containerRect.left;
        let newRatio = (mouseX / containerWidth) * 100;
        const minChatRatio = (MIN_CHAT_WIDTH / containerWidth) * 100;
        const maxChatRatio = 100 - (MIN_EDITOR_WIDTH / containerWidth) * 100;
        newRatio = Math.max(minChatRatio, Math.min(maxChatRatio, newRatio));
        setSplitRatio(newRatio);
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            localStorage.setItem('email_template_split_ratio', splitRatio.toString());
        }
    }, [isDragging, splitRatio]);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        } else {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Fetch template if editing
    useEffect(() => {
        if (!isNew) {
            const fetchTemplate = async () => {
                try {
                    const res = await api.get(`/email-templates/${id}`);
                    const t = res.data.template;
                    setFormData({
                        name: t.name || '',
                        template_type: t.template_type || 'onboarding',
                        subject: t.subject || '',
                        greeting: t.greeting || '',
                        body: t.body || '',
                        closing: t.closing || '',
                        signature: t.signature || '',
                        is_default: t.is_default || false,
                    });
                } catch (error) {
                    toast.error('Failed to load template');
                    navigate('/admin/settings');
                } finally {
                    setLoading(false);
                }
            };
            fetchTemplate();
        }
    }, [id, isNew, navigate]);

    // Auto-scroll AI chat
    useEffect(() => {
        aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [aiMessages]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const insertPlaceholder = (placeholder) => {
        const ref = fieldRefs[activeField]?.current;

        if (ref && typeof ref.insertText === 'function') {
            // It's a RichTextEditor component
            ref.insertText(placeholder);
        } else if (ref && (ref instanceof HTMLInputElement || ref instanceof HTMLTextAreaElement)) {
            // It's a normal text input/textarea
            const start = ref.selectionStart || 0;
            const end = ref.selectionEnd || 0;
            const current = formData[activeField] || '';
            const newValue = current.substring(0, start) + placeholder + current.substring(end);
            handleChange(activeField, newValue);

            // Restore cursor
            setTimeout(() => {
                ref.focus();
                const newPos = start + placeholder.length;
                ref.setSelectionRange(newPos, newPos);
            }, 0);
        } else {
            // Fallback: append to end
            const newValue = (formData[activeField] || '') + placeholder;
            handleChange(activeField, newValue);
        }
        setShowPlaceholders(false);
    };

    const PlaceholderDropdown = ({ field }) => (
        <div className="relative inline-block ml-3 font-normal">
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    setActiveField(field);
                    setShowPlaceholders(showPlaceholders === field ? false : field);
                }}
                className="flex items-center gap-1.5 px-2 py-1 bg-info/10 text-info rounded-md text-xs font-medium hover:bg-info/20 transition-colors"
                title={`Insert placeholder into ${field}`}
            >
                <Type size={12} />
                Insert Placeholder
                <ChevronDown size={12} className={`transition-transform ${showPlaceholders === field ? 'rotate-180' : ''}`} />
            </button>

            {showPlaceholders === field && (
                <div className="absolute z-20 mt-1 left-0 w-64 bg-card border border-border rounded-xl shadow-xl py-2 max-h-64 overflow-y-auto">
                    <div className="px-3 py-1.5 text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                        Insert into: {field}
                    </div>
                    {PLACEHOLDERS.map(p => (
                        <button
                            key={p.value}
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                setActiveField(field);
                                insertPlaceholder(p.value);
                            }}
                            className="w-full text-left px-3 py-1.5 hover:bg-secondary transition-colors flex items-center justify-between"
                        >
                            <span className="text-xs text-foreground">{p.label}</span>
                            <code className="text-[10px] text-muted-foreground bg-secondary rounded px-1 py-0.5">{p.value}</code>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('Template name is required');
            return;
        }
        if (!formData.subject.trim()) {
            toast.error('Subject line is required');
            return;
        }
        if (!formData.body.trim()) {
            toast.error('Body content is required');
            return;
        }

        setSaving(true);
        try {
            if (isNew) {
                await api.post('/email-templates', formData);
                toast.success('Template created!');
            } else {
                await api.put(`/email-templates/${id}`, formData);
                toast.success('Template saved!');
            }
            navigate('/admin/settings?tab=templates');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    const handlePreview = async () => {
        if (isNew && !formData.body.trim()) {
            // Local preview for new unsaved templates
            setPreviewData({
                subject: formData.subject || '(No subject)',
                greeting: formData.greeting || '',
                body: formData.body || '(No content)',
                closing: formData.closing || '',
                signature: formData.signature || '',
            });
            setShowPreview(true);
            return;
        }

        if (!isNew) {
            setPreviewLoading(true);
            try {
                const res = await api.post(`/email-templates/${id}/preview`);
                setPreviewData(res.data.preview);
            } catch (error) {
                toast.error('Failed to generate preview');
            } finally {
                setPreviewLoading(false);
            }
        }
        setShowPreview(true);
    };

    // AI chat handler
    const handleAISend = async () => {
        if (!aiInput.trim() || aiLoading) return;

        const userMessage = aiInput.trim();
        setAiInput('');
        setAiMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setAiLoading(true);

        try {
            // Use the admin AI endpoint to generate email content
            const token = localStorage.getItem('token');
            const baseURL = api.defaults.baseURL;

            const response = await fetch(`${baseURL}/admin/ai/conversations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            });
            const convData = await response.json();
            const convId = convData.conversation?.id;

            if (!convId) throw new Error('Failed to create AI conversation');

            // Send the message with template context
            const templatePurpose = {
                preboarding: `PREBOARDING INVITATION EMAIL
Purpose: Sent to new hires BEFORE their first day to welcome them, set expectations, and prepare them for onboarding.
Tone: Warm, excited, reassuring — the employee hasn't started yet and may feel nervous.
Key content to include: Welcome message, start date confirmation, what to expect on day one, any pre-start tasks (paperwork, equipment setup), who to contact with questions, company culture highlights.
Common use cases: First contact after offer acceptance, reminders about upcoming start date, pre-start checklist completion requests.`,
                onboarding: `ONBOARDING WELCOME EMAIL
Purpose: Sent to employees DURING their onboarding period to guide them through tasks, training, and integration.
Tone: Supportive, informative, encouraging — the employee is actively learning and settling in.
Key content to include: Onboarding task assignments, training schedule, team introductions, resource links, progress encouragement, manager check-in reminders.
Common use cases: Day-one welcome, weekly onboarding progress updates, task assignment notifications, training completion reminders.`,
                active: `ACTIVE EMPLOYEE / USER INVITATION EMAIL
Purpose: Sent to existing employees or new platform users being invited to join the system.
Tone: Professional, clear, action-oriented — the recipient needs to take a specific action (e.g., accept invitation, complete a task).
Key content to include: Purpose of the invitation, what the platform does, how to get started, login instructions, who invited them and why.
Common use cases: Platform invitation for existing staff, role change notifications, new feature announcements, admin/manager account setup.`,
            };

            const contextMessage = `You are an expert HR communications writer specializing in employee onboarding and invitation emails. You are helping write an email template for an HR onboarding platform.

TEMPLATE TYPE & PURPOSE:
${templatePurpose[formData.template_type] || templatePurpose.onboarding}

CURRENT TEMPLATE STATE:
- Name: ${formData.name || '(not set)'}
- Subject: ${formData.subject || '(not set)'}
- Greeting: ${formData.greeting || '(not set)'}
- Body: ${formData.body || '(not set)'}
- Closing: ${formData.closing || '(not set)'}
- Signature: ${formData.signature || '(not set)'}

AVAILABLE PLACEHOLDERS (use these to personalize the email):
${PLACEHOLDERS.map(p => `  ${p.value} — ${p.label}`).join('\n')}

USER REQUEST: ${userMessage}

PLACEHOLDER RULES (CRITICAL — follow these exactly):
- ALWAYS use {employee.first_name} or {employee.full_name} instead of fake/example names like "John" or "Sarah".
- ALWAYS use {company.name} instead of hardcoded company names like "Acme Corp".
- ALWAYS use {sender.name} instead of static sender names.
- Use {employee.start_date} when referencing the employee's start date.
- Use {employee.manager.name} when referencing the employee's manager.
- Use {company.website} when linking to the company site.
- Placeholders MUST use the exact format {placeholder.name} with curly braces.
- When drafting email content, use as many relevant placeholders as possible for personalization.

INSTRUCTIONS:
- Focus specifically on HR onboarding and employee communication best practices.
- Match the tone and purpose described above for the "${formData.template_type}" template type.
- If suggesting full template content, format it clearly with labels: "Subject:", "Greeting:", "Body:", "Closing:", "Signature:" so the user can easily copy each section.
- Keep emails concise, professional, and action-oriented.
- Do NOT suggest generic marketing or sales email content — this is strictly for HR onboarding communications.
- Do NOT use fake names, dates, or company names — always use the placeholders listed above.`;

            const msgResponse = await fetch(`${baseURL}/admin/ai/conversations/${convId}/messages`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ message: contextMessage }),
            });
            const msgData = await msgResponse.json();

            const aiReply = msgData.response?.message || msgData.response?.content || 'Sorry, I couldn\'t generate a response. Please try again.';
            setAiMessages(prev => [...prev, { role: 'assistant', content: aiReply }]);

            // Auto-apply: parse labeled sections from AI response into form fields
            const parseSection = (text, label) => {
                const regex = new RegExp(`(?:^|\\n)\\**${label}:\\**\\s*(.+?)(?=\\n\\**(?:Subject|Greeting|Body|Closing|Signature):\\**|$)`, 'is');
                const match = text.match(regex);
                return match ? match[1].trim() : null;
            };

            const parsed = {
                subject: parseSection(aiReply, 'Subject'),
                greeting: parseSection(aiReply, 'Greeting'),
                body: parseSection(aiReply, 'Body'),
                closing: parseSection(aiReply, 'Closing'),
                signature: parseSection(aiReply, 'Signature'),
            };

            const hasFields = Object.values(parsed).some(v => v);
            if (hasFields) {
                setFormData(prev => ({
                    ...prev,
                    ...(parsed.subject && { subject: parsed.subject }),
                    ...(parsed.greeting && { greeting: parsed.greeting }),
                    ...(parsed.body && { body: parsed.body }),
                    ...(parsed.closing && { closing: parsed.closing }),
                    ...(parsed.signature && { signature: parsed.signature }),
                }));
                toast.success('Template fields updated from AI suggestion');
            }

            // Clean up conversation
            await fetch(`${baseURL}/admin/ai/conversations/${convId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch (error) {
            console.error('AI error:', error);
            setAiMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.'
            }]);
        } finally {
            setAiLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    const typeLabel = formData.template_type.charAt(0).toUpperCase() + formData.template_type.slice(1);

    return (
        <div className="h-[calc(100vh-4rem)] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-border bg-card">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/admin/settings?tab=templates')}
                        className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-lg font-bold text-foreground">
                            {isNew ? `New ${typeLabel} Template` : 'Edit Template'}
                        </h1>
                        <p className="text-xs text-muted-foreground">
                            {formData.template_type === 'preboarding' && 'Sent to employees before their start date'}
                            {formData.template_type === 'onboarding' && 'Sent to employees during onboarding'}
                            {formData.template_type === 'active' && 'Sent to existing employees or admin/manager users'}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowAI(!showAI)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${showAI ? 'bg-accent text-white' : 'bg-accent/10 text-accent hover:bg-accent/20'}`}
                    >
                        <Sparkles size={16} />
                        <span className="hidden sm:inline">AI Assist</span>
                    </button>
                    <button
                        onClick={handlePreview}
                        disabled={previewLoading}
                        className="flex items-center gap-2 px-3 py-2 bg-secondary text-foreground rounded-lg hover:bg-secondary/80 transition-colors text-sm font-medium"
                    >
                        {previewLoading ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
                        <span className="hidden sm:inline">Preview</span>
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors text-sm font-medium shadow-lg shadow-accent/30"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        {isNew ? 'Create' : 'Save'}
                    </button>
                </div>
            </div>

            {/* Content area */}
            <div className="flex-1 flex overflow-hidden" ref={containerRef}>
                {/* AI Panel (left side) */}
                {showAI && (
                    <div
                        className="hidden lg:flex flex-col border-r border-border bg-card"
                        style={{
                            width: `${splitRatio}%`,
                            transition: isDragging ? 'none' : 'width 300ms ease',
                        }}
                    >
                        {/* AI Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                            <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                                    <Sparkles size={14} className="text-white" />
                                </div>
                                <span className="text-sm font-semibold text-foreground">AI Email Assistant</span>
                            </div>
                            <button
                                onClick={() => setShowAI(false)}
                                className="p-1 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors"
                            >
                                <EyeOff size={16} />
                            </button>
                        </div>

                        {/* Chat messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {aiMessages.length === 0 && (
                                <div className="text-center py-8">
                                    <div className="w-12 h-12 mx-auto rounded-2xl bg-gradient-to-br from-violet-500/20 to-purple-600/20 flex items-center justify-center mb-3">
                                        <Sparkles size={20} className="text-violet-400" />
                                    </div>
                                    <p className="text-sm font-medium text-foreground mb-1">AI Email Assistant</p>
                                    <p className="text-xs text-muted-foreground max-w-[240px] mx-auto">
                                        Ask me to draft your {formData.template_type} email, improve the subject line, adjust tone, or suggest placeholders.
                                    </p>
                                    <div className="mt-4 space-y-2">
                                        {(formData.template_type === 'preboarding' ? [
                                            'Draft a warm pre-start welcome email',
                                            'Write a "what to expect on day one" email',
                                            'Create a pre-boarding checklist reminder',
                                        ] : formData.template_type === 'onboarding' ? [
                                            'Draft a first-day welcome email',
                                            'Write an onboarding task assignment email',
                                            'Create an encouraging progress check-in',
                                        ] : [
                                            'Draft a platform invitation email',
                                            'Write a professional account setup email',
                                            'Create a team role announcement email',
                                        ]).map((s, i) => (
                                            <button
                                                key={i}
                                                onClick={() => { setAiInput(s); }}
                                                className="block w-full text-left px-3 py-2 bg-secondary rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
                                            >
                                                "{s}"
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {aiMessages.map((msg, i) => (
                                <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                                    {msg.role === 'assistant' && (
                                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <Bot size={12} className="text-white" />
                                        </div>
                                    )}
                                    <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap ${msg.role === 'user'
                                        ? 'bg-accent text-white rounded-br-md'
                                        : 'bg-secondary text-foreground rounded-bl-md'
                                        }`}>
                                        {msg.role === 'assistant'
                                            ? renderWithPlaceholderChips(msg.content)
                                            : msg.content}
                                    </div>
                                    {msg.role === 'user' && (
                                        <div className="w-6 h-6 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <User size={12} className="text-accent" />
                                        </div>
                                    )}
                                </div>
                            ))}

                            {aiLoading && (
                                <div className="flex gap-2">
                                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Bot size={12} className="text-white" />
                                    </div>
                                    <div className="px-3 py-2 bg-secondary rounded-xl rounded-bl-md">
                                        <div className="flex gap-1">
                                            <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                            <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                            <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={aiChatEndRef} />
                        </div>

                        {/* AI Input */}
                        <div className="p-3 border-t border-border">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={aiInput}
                                    onChange={(e) => setAiInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAISend()}
                                    placeholder="Ask AI for help..."
                                    className="flex-1 px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50"
                                    disabled={aiLoading}
                                />
                                <button
                                    onClick={handleAISend}
                                    disabled={aiLoading || !aiInput.trim()}
                                    className="px-3 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50"
                                >
                                    <Send size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Draggable Divider */}
                {showAI && (
                    <div
                        className="hidden lg:flex flex-col items-center justify-center w-1 bg-border hover:bg-accent/20 cursor-col-resize transition-colors group z-20"
                        onMouseDown={handleMouseDown}
                    >
                        <div className="absolute flex flex-col items-center justify-center w-4 h-12 rounded bg-secondary group-hover:bg-accent transition-colors shadow-md border border-border group-hover:border-accent">
                            <GripVertical className="w-3 h-3 text-muted-foreground group-hover:text-white" />
                        </div>
                    </div>
                )}

                {/* Template Form (right side) */}
                <div
                    className="flex-1 overflow-y-auto p-4 lg:p-6"
                    style={{
                        width: showAI && window.innerWidth >= 1024 ? `${100 - splitRatio}%` : '100%',
                        transition: isDragging ? 'none' : 'width 300ms ease',
                    }}
                >
                    <div className="max-w-3xl mx-auto space-y-6">
                        {/* Template Name */}
                        <div>
                            <label className="flex items-center text-sm font-medium text-foreground mb-1.5">
                                Template Name
                                <PlaceholderDropdown field="name" />
                            </label>
                            <AutosuggestInput
                                inputRef={fieldRefs.name}
                                value={formData.name}
                                onChange={(val) => handleChange('name', val)}
                                onFocus={() => setActiveField('name')}
                                placeholder="e.g. Welcome Email - Friendly"
                                className="w-full px-4 py-2.5 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all"
                            />
                        </div>

                        {/* Type selector (new only) */}
                        {isNew && (
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1.5">
                                    Template Type
                                </label>
                                <select
                                    value={formData.template_type}
                                    onChange={(e) => handleChange('template_type', e.target.value)}
                                    className="w-full px-4 py-2.5 bg-secondary border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all"
                                >
                                    <option value="preboarding">Preboarding</option>
                                    <option value="onboarding">Onboarding</option>
                                    <option value="active">Active / User Invitation</option>
                                </select>
                            </div>
                        )}

                        {/* Subject */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-1.5">
                                <span className="flex items-center gap-2">
                                    <Mail size={14} />
                                    Subject Line
                                </span>
                                <PlaceholderDropdown field="subject" />
                            </label>
                            <AutosuggestInput
                                inputRef={fieldRefs.subject}
                                value={formData.subject}
                                onChange={(val) => handleChange('subject', val)}
                                onFocus={() => setActiveField('subject')}
                                placeholder="e.g. Welcome to {company.name}, {employee.first_name}!"
                                className="w-full px-4 py-2.5 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all"
                            />
                        </div>

                        {/* Greeting */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-1.5">
                                <span className="flex items-center gap-2">
                                    <MessageSquare size={14} />
                                    Greeting
                                </span>
                                <PlaceholderDropdown field="greeting" />
                            </label>
                            <AutosuggestInput
                                inputRef={fieldRefs.greeting}
                                value={formData.greeting}
                                onChange={(val) => handleChange('greeting', val)}
                                onFocus={() => setActiveField('greeting')}
                                placeholder="e.g. Hi {employee.first_name},"
                                className="w-full px-4 py-2.5 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all"
                            />
                        </div>

                        {/* Body */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-1.5">
                                <span className="flex items-center gap-2">
                                    <AlignLeft size={14} />
                                    Email Body
                                </span>
                                <PlaceholderDropdown field="body" />
                            </label>
                            <div onFocus={() => setActiveField('body')}>
                                <RichTextEditor
                                    ref={fieldRefs.body}
                                    value={formData.body}
                                    onChange={(value) => handleChange('body', value)}
                                />
                            </div>
                        </div>

                        {/* Closing */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-1.5">
                                Closing
                                <PlaceholderDropdown field="closing" />
                            </label>
                            <div onFocus={() => setActiveField('closing')}>
                                <RichTextEditor
                                    ref={fieldRefs.closing}
                                    value={formData.closing}
                                    onChange={(value) => handleChange('closing', value)}
                                />
                            </div>
                        </div>

                        {/* Signature */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-1.5">
                                Signature
                                <PlaceholderDropdown field="signature" />
                            </label>
                            <div onFocus={() => setActiveField('signature')}>
                                <RichTextEditor
                                    ref={fieldRefs.signature}
                                    value={formData.signature}
                                    onChange={(value) => handleChange('signature', value)}
                                />
                            </div>
                        </div>

                        {/* Default toggle */}
                        <div className="flex items-center gap-3 p-4 bg-secondary rounded-xl border border-border">
                            <button
                                onClick={() => handleChange('is_default', !formData.is_default)}
                                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${formData.is_default ? 'bg-accent' : 'bg-muted'}`}
                            >
                                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${formData.is_default ? 'left-7' : 'left-1'}`} />
                            </button>
                            <div>
                                <p className="text-sm font-medium text-foreground">Set as default template</p>
                                <p className="text-xs text-muted-foreground">This template will be pre-selected when adding new {typeLabel.toLowerCase()} employees</p>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* Preview Modal */}
            {showPreview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                            <div className="flex items-center gap-2">
                                <Eye size={18} className="text-accent" />
                                <h3 className="text-lg font-semibold text-foreground">Email Preview</h3>
                            </div>
                            <button
                                onClick={() => setShowPreview(false)}
                                className="p-1 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {previewData ? (
                                <div className="bg-white rounded-xl p-6 text-gray-800 shadow-inner">
                                    <div className="border-b border-gray-200 pb-3 mb-4">
                                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Subject</p>
                                        <p className="text-lg font-medium">{previewData.subject}</p>
                                    </div>
                                    {previewData.greeting && (
                                        <p className="text-base mb-3" dangerouslySetInnerHTML={{ __html: previewData.greeting }} />
                                    )}
                                    <div className="text-base leading-relaxed whitespace-pre-wrap mb-4" dangerouslySetInnerHTML={{ __html: previewData.body }} />
                                    {previewData.closing && (
                                        <p className="text-base mb-3" dangerouslySetInnerHTML={{ __html: previewData.closing }} />
                                    )}
                                    {previewData.signature && (
                                        <div className="border-t border-gray-200 pt-3 mt-4 text-sm text-gray-600 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: previewData.signature }} />
                                    )}
                                </div>
                            ) : (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 size={24} className="animate-spin text-muted-foreground" />
                                </div>
                            )}
                        </div>

                        <div className="px-6 py-3 border-t border-border flex justify-end">
                            <button
                                onClick={() => setShowPreview(false)}
                                className="px-4 py-2 bg-secondary text-foreground rounded-lg hover:bg-secondary/80 transition-colors text-sm font-medium"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailTemplateEditor;
